import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { User, Store, Loader2 } from 'lucide-react';

/**
 * VendorCard - Allows admins to assign a listing to a specific vendor
 * Shows selected vendor name and store info
 */
const VendorCard = ({ formData, handleChange }) => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVendor, setSelectedVendor] = useState(null);

  useEffect(() => {
    fetchVendors();
  }, []);

  // Update selected vendor when formData.vendor_id changes
  useEffect(() => {
    if (formData.vendor_id && vendors.length > 0) {
      const vendor = vendors.find(v => (v.id || v._id) === formData.vendor_id);
      setSelectedVendor(vendor || null);
    }
  }, [formData.vendor_id, vendors]);

  const fetchVendors = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const token = sessionStorage.getItem('token');
      
      const response = await fetch(`${BACKEND_URL}/api/vendors/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVendors(data || []);
        
        // If vendor_id is already set, find and set the selected vendor
        if (formData.vendor_id) {
          const vendor = data.find(v => (v.id || v._id) === formData.vendor_id);
          setSelectedVendor(vendor || null);
        }
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVendorChange = (vendorId) => {
    handleChange('vendor_id', vendorId);
    const vendor = vendors.find(v => (v.id || v._id) === vendorId);
    setSelectedVendor(vendor || null);
  };

  const getVendorDisplayName = (vendor) => {
    if (!vendor) return '';
    // Priority: identity.store_name (from Store Identity step) > vendor_profile store paths > personal name
    return vendor.identity?.store_name || 
           vendor.vendor_profile?.store?.store_name || 
           vendor.vendor_profile?.store_name || 
           vendor.store_name || 
           vendor.business_name || 
           vendor.name || 
           vendor.email;
  };

  const getVendorSecondaryInfo = (vendor) => {
    if (!vendor) return null;
    const storeName = vendor.identity?.store_name || vendor.vendor_profile?.store?.store_name || vendor.vendor_profile?.store_name || vendor.store_name;
    const name = vendor.name;
    
    // If store name is different from display name, show it
    if (storeName && storeName !== getVendorDisplayName(vendor)) {
      return storeName;
    }
    if (name && name !== getVendorDisplayName(vendor)) {
      return name;
    }
    return vendor.email;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <User className="h-4 w-4" />
            Vendor Assignment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading vendors...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <User className="h-4 w-4" />
          Vendor Assignment
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label className="text-sm">Assign to Vendor</Label>
          <Select
            value={formData.vendor_id || ''}
            onValueChange={handleVendorChange}
          >
            <SelectTrigger data-testid="vendor-select">
              <SelectValue placeholder="Select vendor" />
            </SelectTrigger>
            <SelectContent>
              {vendors.map((vendor) => (
                <SelectItem key={vendor.id || vendor._id} value={vendor.id || vendor._id}>
                  <div className="flex items-center gap-2">
                    <Store className="h-4 w-4 text-gray-400" />
                    <span>{getVendorDisplayName(vendor)}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Show selected vendor details */}
        {selectedVendor && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-full">
                <Store className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-blue-900">
                  {getVendorDisplayName(selectedVendor)}
                </p>
                <p className="text-sm text-blue-700">
                  {getVendorSecondaryInfo(selectedVendor)}
                </p>
                {selectedVendor.vendor_profile?.kyc_status && (
                  <Badge 
                    variant="outline" 
                    className={`mt-1 text-xs ${
                      selectedVendor.vendor_profile.kyc_status === 'verified' 
                        ? 'bg-green-50 text-green-700 border-green-200' 
                        : 'bg-yellow-50 text-yellow-700 border-yellow-200'
                    }`}
                  >
                    KYC: {selectedVendor.vendor_profile.kyc_status}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        )}

        <p className="text-xs text-gray-500">
          As admin, you can create listings on behalf of vendors.
        </p>
      </CardContent>
    </Card>
  );
};

export default VendorCard;
